<template>
  <section v-if="tagsList.length > 0" :aria-labelledby="sectionTitleId">
    <div class="mb-12 flex shrink-0 items-center gap-4">
      <h2 :id="sectionTitleId" class="heading-2 m-0">
        {{ i18n(translations.title) }}
      </h2>

      <RevTooltip class="inline-flex shrink-0 items-center">
        <template #trigger="{ show, hide }">
          <IconInfo
            class="inline-flex shrink-0"
            size="16"
            @focusin="show"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="show"
          />
        </template>

        {{ sectionTooltipInfo }}
      </RevTooltip>
    </div>

    <RevCard class="flex flex-wrap items-center gap-y-8 p-16">
      <template v-for="(tag, index) in tagsList" :key="tag.id">
        <span
          v-if="index > 0"
          aria-hidden="true"
          class="mx-8 h-[20px] max-h-[20px] w-0 shrink-0 self-center border-0 border-l border-solid border-static-default-low"
        />

        <span
          class="label-large text-static-default-hi flex max-w-full items-center gap-4 py-4"
        >
          <component
            :is="tag.icon"
            aria-hidden="true"
            class="size-20 shrink-0"
            :class="sentimentIconClass(tag.sentiment)"
          />
          <span class="body-1-bold">{{ tag.label }}</span>
        </span>
      </template>
    </RevCard>
  </section>
</template>

<script lang="ts" setup>
import { type Component, computed, toRefs } from 'vue'

import type { ReviewsTagDetails } from '@backmarket/http-api/src/api-specs-reviews/types/ai-summary'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import { AI_SUMMARY_REVIEW_COUNT_THRESHOLD } from '../../aiSummary.constants'
import { formatRawTag, sentimentToIcon, tagToLabel } from '../AITags/helpers'

import translations from './HighlightedAITags.translations'

const props = withDefaults(
  defineProps<{
    tags: ReviewsTagDetails[]
    updatedAt?: string | null
    reviewsCount?: number | null
  }>(),
  {
    updatedAt: undefined,
    reviewsCount: undefined,
  },
)

const { tags, updatedAt, reviewsCount } = toRefs(props)

const i18n = useI18n()

const sectionTitleId = 'highlighted-ai-tags-title'

type TagsListItem = {
  id: ReviewsTagDetails['tag']
  label: string
  icon: Component
  sentiment: ReviewsTagDetails['sentiment']
}

const tagsList = computed<TagsListItem[]>(() => {
  return tags.value.map((detail) => {
    const label = i18n(tagToLabel[detail.tag]) ?? formatRawTag(detail.tag)

    return {
      id: detail.tag,
      label,
      icon: sentimentToIcon[detail.sentiment],
      sentiment: detail.sentiment,
    }
  })
})

function sentimentIconClass(sentiment: ReviewsTagDetails['sentiment']) {
  return sentiment === 'positive' || sentiment === 'neutral'
    ? 'text-static-success-hi'
    : 'text-static-default-low'
}

const formattedUpdatedAt = computed(() => {
  if (!updatedAt.value) return ''

  return i18n.date(new Date(updatedAt.value))
})

const sectionTooltipInfo = computed(() => {
  if (
    !reviewsCount.value ||
    reviewsCount.value < AI_SUMMARY_REVIEW_COUNT_THRESHOLD
  ) {
    return i18n(translations.tooltipInfoAllReviews, {
      updateDate: formattedUpdatedAt.value,
    })
  }

  return i18n(translations.tooltipInfoLatestReviews, {
    number: AI_SUMMARY_REVIEW_COUNT_THRESHOLD,
    updateDate: formattedUpdatedAt.value,
  })
})
</script>
